import { useState, useRef, useEffect, useCallback } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '@/context/AuthContext'
import { useMagnum } from '@/context/MagnumContext'
import { apiFetch } from '@/lib/apiFetch'
import { useExerciseSession } from '@/hooks/useExerciseSession'
import { fetchMagnumMoment, EffectData } from '@/lib/magnumMoment'
import EffectRenderer from '@/components/EffectRenderer'
import MusicStaff from '@/components/MusicStaff'
import { useMusicPlayback } from '@/hooks/useMusicPlayback'
import {
  generateMelody,
  inputNotesToAbc,
  gradeMelody,
  buildGradedAbc,
  melodyTierForLevel,
  inferOctave,
  noteNameToMidi,
  DURATION_SYMBOLS,
  type GeneratedMelody,
  type MelodyNote,
  type NoteDuration,
  type GradeResult,
} from '@/lib/melodyGeneration'
import type { ClefPreference } from '@/lib/musicTheory'

type GameState = 'idle' | 'loading' | 'playing' | 'submitted' | 'done'

const QUESTIONS_PER_SESSION = 5
const MAX_PLAYS = 4
const STREAK_MILESTONES = [3, 5]
const PITCH_CLASSES = ['C', 'D', 'E', 'F', 'G', 'A', 'B'] as const

export default function MelodicDictationPage() {
  const { user } = useAuth()
  const magnum = useMagnum()
  const { sessionId, stats, activeMs, isIdle, startSession, endSession, recordAttempt, dismissIdle } =
    useExerciseSession({ exerciseType: 'MELODIC_DICTATION' })
  const { playInterval, stop, isPlaying } = useMusicPlayback()

  const [state, setState] = useState<GameState>('idle')
  const [melodies, setMelodies] = useState<GeneratedMelody[]>([])
  const [currentIndex, setCurrentIndex] = useState(0)
  const [inputNotes, setInputNotes] = useState<MelodyNote[]>([])
  const [selectedDuration, setSelectedDuration] = useState<NoteDuration>('quarter')
  const [accidental, setAccidental] = useState<'natural' | 'sharp' | 'flat'>('natural')
  const [currentOctave, setCurrentOctave] = useState(4)
  const [playsRemaining, setPlaysRemaining] = useState(MAX_PLAYS)
  const [gradeResult, setGradeResult] = useState<GradeResult | null>(null)
  const [activeEffects, setActiveEffects] = useState<EffectData[] | null>(null)
  const questionStartRef = useRef(Date.now())
  const settingsRef = useRef<{ clefPreference: ClefPreference; difficultyLevel: number }>({
    clefPreference: 'TREBLE',
    difficultyLevel: 1.0,
  })

  const currentMelody = melodies[currentIndex] || null
  const tierConfig = currentMelody
    ? melodyTierForLevel(currentMelody.config.tier)
    : melodyTierForLevel(1)
  const allowedDurations = tierConfig.durations

  const startGame = async () => {
    setState('loading')
    try {
      const [musicSettings, diffData] = await Promise.all([
        apiFetch('/music/settings/mine'),
        apiFetch('/game-difficulty/mine/MELODIC_DICTATION'),
        startSession(),
      ])
      settingsRef.current = {
        clefPreference: musicSettings.clefPreference,
        difficultyLevel: diffData.difficultyLevel,
      }

      const generated: GeneratedMelody[] = []
      for (let i = 0; i < QUESTIONS_PER_SESSION; i++) {
        generated.push(generateMelody(diffData.difficultyLevel, musicSettings.clefPreference))
      }

      setMelodies(generated)
      setCurrentIndex(0)
      resetForNewMelody(generated[0])
      setState('playing')

      fetchMagnumMoment({
        contextType: 'GAME_STARTED',
        exerciseType: 'MELODIC_DICTATION',
        category: 'MUSIC',
        streak: 0,
        sessionId,
      })
        .then((m) => magnum.handleEngineResponse(m, 'happy'))
        .catch(() => {})
    } catch {
      setState('idle')
    }
  }

  const resetForNewMelody = (melody: GeneratedMelody) => {
    const firstNote = melody.notes[0]
    setInputNotes([firstNote])
    setSelectedDuration('quarter')
    setAccidental('natural')
    setCurrentOctave(Math.floor(firstNote.midi / 12) - 1)
    setPlaysRemaining(MAX_PLAYS)
    setGradeResult(null)
    questionStartRef.current = Date.now()
  }

  const handlePlay = async () => {
    if (!currentMelody || isPlaying) return
    if (state !== 'submitted' && playsRemaining <= 0) return
    if (state !== 'submitted') setPlaysRemaining(p => p - 1)
    await playInterval(currentMelody.abc, 80)
  }

  const addNote = useCallback((pitchClass: string) => {
    if (state !== 'playing' || !currentMelody) return

    const prevMidi = inputNotes.length > 0 ? inputNotes[inputNotes.length - 1].midi : 60
    const oct = inferOctave(pitchClass, accidental, prevMidi, currentMelody.config.clef)
    const midi = noteNameToMidi(pitchClass, accidental, oct)

    setInputNotes(prev => [...prev, { midi, duration: selectedDuration }])
    setCurrentOctave(oct)
    setAccidental('natural')
  }, [state, currentMelody, inputNotes, accidental, selectedDuration])

  const undoNote = useCallback(() => {
    if (state !== 'playing') return
    setInputNotes(prev => (prev.length > 1 ? prev.slice(0, -1) : prev))
  }, [state])

  const handleSubmit = async () => {
    if (!currentMelody || state !== 'playing') return

    const result = gradeMelody(currentMelody.notes, inputNotes)
    setGradeResult(result)
    setState('submitted')

    const responseMs = Date.now() - questionStartRef.current
    const correct = result.score >= 0.8

    const newStreak = correct ? stats.streak + 1 : 0
    const isMilestone = correct && STREAK_MILESTONES.includes(newStreak)
    const contextType = correct
      ? isMilestone ? 'STREAK_MILESTONE' : 'CORRECT_ANSWER'
      : 'WRONG_ANSWER'

    fetchMagnumMoment({
      contextType,
      exerciseType: 'MELODIC_DICTATION',
      category: 'MUSIC',
      correct,
      responseMs,
      activeMs,
      streak: newStreak,
      sessionId,
    })
      .then((moment) => {
        magnum.handleEngineResponse(moment, correct ? 'happy' : 'encouraging')
        if (moment.effects?.length) setActiveEffects(moment.effects)
      })
      .catch(() => {})

    await recordAttempt(null, correct, responseMs, {
      score: result.score,
      correctCount: result.correctCount,
      totalCount: result.totalCount,
      key: currentMelody.config.key.name,
      timeSignature: currentMelody.config.timeSignature,
      clef: currentMelody.config.clef,
    })
  }

  const nextMelody = () => {
    stop()
    const next = currentIndex + 1
    if (next >= melodies.length) {
      setState('done')
      fetchMagnumMoment({
        contextType: 'SESSION_END',
        exerciseType: 'MELODIC_DICTATION',
        category: 'MUSIC',
        streak: stats.streak,
        sessionId,
        activeMs,
      })
        .then((m) => magnum.handleEngineResponse(m, 'excited'))
        .catch(() => {})
      endSession()
    } else {
      setCurrentIndex(next)
      resetForNewMelody(melodies[next])
      setState('playing')
    }
  }

  useEffect(() => {
    magnum.setFrameIdle(isIdle)
    if (isIdle) {
      fetchMagnumMoment({
        contextType: 'IDLE_RETURN',
        exerciseType: 'MELODIC_DICTATION',
        category: 'MUSIC',
        streak: stats.streak,
        sessionId,
        activeMs,
      })
        .then((m) => magnum.handleEngineResponse(m))
        .catch(() => magnum.say("Still here. Still waiting. Still adorable."))
    }
  }, [isIdle])

  useEffect(() => {
    startGame()
  }, [])

  useEffect(() => {
    if (isIdle && state === 'submitted') dismissIdle()
  }, [state])

  useEffect(() => {
    if (state !== 'playing') return
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return

      const key = e.key.toUpperCase()
      if (PITCH_CLASSES.includes(key as typeof PITCH_CLASSES[number])) {
        e.preventDefault()
        addNote(key)
        return
      }

      if (e.key === 'Backspace') {
        e.preventDefault()
        undoNote()
        return
      }
      if (e.key === 'Enter') {
        e.preventDefault()
        if (inputNotes.length > 1) handleSubmit()
        return
      }
      if (e.key === ' ') {
        e.preventDefault()
        handlePlay()
        return
      }

      if (e.key === 'ArrowUp') {
        e.preventDefault()
        if (e.shiftKey) {
          setCurrentOctave(o => Math.min(o + 1, 7))
        } else {
          setAccidental(a => a === 'natural' ? 'sharp' : a === 'sharp' ? 'natural' : 'natural')
        }
        return
      }
      if (e.key === 'ArrowDown') {
        e.preventDefault()
        if (e.shiftKey) {
          setCurrentOctave(o => Math.max(o - 1, 1))
        } else {
          setAccidental(a => a === 'natural' ? 'flat' : a === 'flat' ? 'natural' : 'natural')
        }
        return
      }

      const numKey = parseInt(e.key)
      if (numKey >= 1 && numKey <= allowedDurations.length) {
        e.preventDefault()
        setSelectedDuration(allowedDurations[numKey - 1])
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [state, addNote, undoNote, allowedDurations, inputNotes.length])

  if (!user || user.type !== 'child') return null

  if (state === 'loading') {
    return (
      <div className="py-16 text-center">
        <p className="text-lg text-muted-foreground">Loading...</p>
      </div>
    )
  }

  if (state === 'done') {
    const accuracy = stats.total > 0 ? Math.round((stats.correct / stats.total) * 100) : 0
    return (
      <div className="py-8 text-center space-y-8">
        <div className="text-6xl">🎶</div>
        <h1 className="text-3xl font-bold">Session Complete!</h1>
        <div className="grid grid-cols-3 gap-4 max-w-sm mx-auto">
          <div className="p-4 border border-border rounded-lg">
            <p className="text-2xl font-bold text-primary">{stats.correct}</p>
            <p className="text-xs text-muted-foreground">Correct</p>
          </div>
          <div className="p-4 border border-border rounded-lg">
            <p className="text-2xl font-bold">{stats.total}</p>
            <p className="text-xs text-muted-foreground">Total</p>
          </div>
          <div className="p-4 border border-border rounded-lg">
            <p className="text-2xl font-bold text-primary">{accuracy}%</p>
            <p className="text-xs text-muted-foreground">Accuracy</p>
          </div>
        </div>
        <div className="flex gap-4 justify-center pt-4">
          <button
            onClick={startGame}
            className="px-6 py-3 bg-primary text-primary-foreground rounded-xl font-bold hover:opacity-90 transition"
          >
            Play Again
          </button>
          <Link
            to="/practice"
            className="px-6 py-3 border border-border rounded-xl font-bold hover:bg-accent transition inline-flex items-center"
          >
            Back to Exercises
          </Link>
        </div>
      </div>
    )
  }

  const inputAbc = currentMelody
    ? inputNotesToAbc(inputNotes, currentMelody.config)
    : ''

  const gradedData = (state === 'submitted' && gradeResult && currentMelody)
    ? buildGradedAbc(inputNotes, currentMelody.notes, currentMelody.config, gradeResult)
    : null

  const isPerfect = gradeResult?.score === 1

  return (
    <div className="text-center space-y-4">
      <EffectRenderer effects={activeEffects} onDone={() => setActiveEffects(null)} />

      <div className="flex items-center justify-between">
        <Link to="/practice" className="text-sm text-muted-foreground hover:text-foreground transition">
          &larr; Back
        </Link>
        <div className="flex items-center gap-4 text-sm text-muted-foreground">
          <span>{currentIndex + 1} of {melodies.length}</span>
          <span className="text-primary font-medium">{stats.correct} correct</span>
          {stats.streak >= 3 && <span className="text-amber-500 font-medium">🔥 {stats.streak}</span>}
        </div>
      </div>

      <div className="w-full bg-accent rounded-full h-2">
        <div
          className="bg-primary h-2 rounded-full transition-all duration-300"
          style={{
            width: `${((currentIndex + (state === 'submitted' ? 1 : 0)) / melodies.length) * 100}%`,
          }}
        />
      </div>

      {currentMelody && (
        <>
          <div className="space-y-1">
            <p className="text-xs text-muted-foreground">
              {currentMelody.config.key.name} &middot; {currentMelody.config.timeSignature}
            </p>
          </div>

          <div className="bg-white rounded-xl p-3 border border-border">
            {gradedData ? (
              <MusicStaff
                abc={gradedData.abc}
                width={500}
                greenIndices={gradedData.greenIndices}
                redIndices={gradedData.redIndices}
              />
            ) : (
              <MusicStaff abc={inputAbc} width={500} />
            )}
          </div>

          {state === 'submitted' && gradeResult ? (
            <>
              <div className={`text-lg font-semibold ${isPerfect ? 'text-primary' : 'text-amber-500'}`}>
                {isPerfect
                  ? 'Perfect!'
                  : `${gradeResult.correctCount}/${gradeResult.totalCount} notes correct`}
              </div>

              {!isPerfect && currentMelody && (
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Correct melody:</p>
                  <div className="bg-white rounded-xl p-3 border border-border">
                    <MusicStaff abc={currentMelody.abc} width={500} />
                  </div>
                </div>
              )}

              <div className="flex justify-center gap-3">
                <button
                  onClick={handlePlay}
                  disabled={isPlaying}
                  className="px-5 py-3 border-2 border-border rounded-xl hover:border-primary hover:bg-accent transition disabled:opacity-50 flex items-center gap-2"
                >
                  <span className="text-xl">{isPlaying ? '🔊' : '🎵'}</span>
                  <span className="text-sm font-medium">Replay</span>
                </button>
                <button
                  onClick={nextMelody}
                  className="px-6 py-3 bg-primary text-primary-foreground rounded-xl font-bold hover:opacity-90 transition"
                >
                  {currentIndex + 1 >= melodies.length ? 'See Results' : 'Next'}
                </button>
              </div>
            </>
          ) : (
            <>
              <div className="flex justify-center">
                <button
                  onClick={handlePlay}
                  disabled={isPlaying || playsRemaining <= 0}
                  className="px-6 py-3 border-2 border-border rounded-xl hover:border-primary hover:bg-accent transition disabled:opacity-50 flex items-center gap-2"
                >
                  <span className="text-2xl">{isPlaying ? '🔊' : '🎵'}</span>
                  <span className="text-sm font-medium">
                    {playsRemaining > 0 ? `Play (${playsRemaining} left)` : 'No plays left'}
                  </span>
                </button>
              </div>

              <div className="space-y-3 max-w-md mx-auto">
                <div className="flex justify-center gap-2">
                  {allowedDurations.map((d, i) => (
                    <button
                      key={d}
                      onClick={() => setSelectedDuration(d)}
                      className={`px-3 py-2 rounded-lg text-lg transition min-h-[44px] min-w-[44px] ${
                        selectedDuration === d
                          ? 'bg-primary text-primary-foreground'
                          : 'border border-border hover:bg-accent'
                      }`}
                      title={`${d} (${i + 1})`}
                    >
                      {DURATION_SYMBOLS[d]}
                    </button>
                  ))}
                </div>

                <div className="flex justify-center gap-1.5">
                  {PITCH_CLASSES.map(pc => (
                    <button
                      key={pc}
                      onClick={() => addNote(pc)}
                      className="w-11 h-11 rounded-lg border border-border text-lg font-bold hover:bg-accent hover:border-primary transition min-h-[44px]"
                    >
                      {pc}
                    </button>
                  ))}
                </div>

                <div className="flex justify-center gap-2">
                  {(['natural', 'sharp', 'flat'] as const).map(a => (
                    <button
                      key={a}
                      onClick={() => setAccidental(a)}
                      className={`px-3 py-2 rounded-lg text-sm font-medium transition min-h-[44px] ${
                        accidental === a
                          ? 'bg-primary text-primary-foreground'
                          : 'border border-border hover:bg-accent'
                      }`}
                    >
                      {a === 'natural' ? '♮' : a === 'sharp' ? '♯' : '♭'}
                    </button>
                  ))}
                  <div className="border-l border-border mx-1" />
                  <button
                    onClick={undoNote}
                    disabled={inputNotes.length <= 1}
                    className="px-3 py-2 rounded-lg text-sm font-medium border border-border hover:bg-accent transition disabled:opacity-30 min-h-[44px]"
                  >
                    Undo
                  </button>
                  <button
                    onClick={handleSubmit}
                    disabled={inputNotes.length <= 1}
                    className="px-4 py-2 rounded-lg text-sm font-bold bg-primary text-primary-foreground hover:opacity-90 transition disabled:opacity-30 min-h-[44px]"
                  >
                    Submit
                  </button>
                </div>
              </div>
            </>
          )}
        </>
      )}
    </div>
  )
}
